import * as React from 'react';
import { ButtonProps, ButtonTypeMap, Stack, Typography } from '@mui/material';
import { find, lowerCase } from 'lodash';
import { useContext, useEffect, useMemo } from 'react';
import { useMixpanel } from 'react-mixpanel-browser';
import { SetOptional } from 'type-fest';

import {
    DeviceSignalStatus,
    DeviceSubscriptionStatus,
    SpotterSystemError,
    useDeviceCellularSignalStatus,
    useDeviceSignalStatus,
    useDeviceSubscriptionStatus,
} from '@/api/devices';
import { CELLULAR_ALERT_MESSAGING, INSUFFICIENT_SIGNAL_ERRORS } from '@/components/spotter/CellularErrorAlert';
import {
    AlertContext,
    InlineAlert,
    InlineAlertBody,
    InlineAlertPrimaryButton,
    InlineAlertSecondaryButton,
    InlineAlertTitle,
} from '@/components/ui/InlineAlert';
import { useNotifications } from '@/hooks/useNotifications';
import { spotterIdHash } from '@/hooks/useSpotterHash';
import { contactSupportHref } from '@/utils/urlHelpers';

export const LEARN_MORE_URL_ENABLE_SAT_FALLBACK =
    'https://sofarocean.notion.site/Spotter-Data-Subscription-Renewals-and-Pricing-58a4fa5351f44bd48e8b41bbda41ac5f#ceaa80a902bd4ce197b561ecb9c2d953';
export const LEARN_MORE_URL_CELL_NOT_WORKING =
    'https://sofarocean.notion.site/Cellular-Support-Documentation-0fd67a99122b43c89563c57340ed165f#2118ff95945080b7a2a6e7bcf6f67717';

export type TelemetryServiceMessage = {
    notificationId: string;
    type: 'info' | 'error';
    title: string;
    body: string;
    /**
     * Absent defaults to true
     */
    dismissable?: boolean;
    buttons: (props: Pick<StatusMessageButtonProps, 'spotterId' | 'notificationId'>) => JSX.Element;
};

type StatusMessageButtonProps<RootComponent extends React.ElementType = ButtonTypeMap['defaultComponent']> =
    ButtonProps<RootComponent, { component?: RootComponent }> & {
        spotterId: string;
        notificationId: string;
        rank: 'primary' | 'secondary';
    };

const StatusMessageButton = ({ onClick, rank, spotterId, notificationId, ...props }: StatusMessageButtonProps) => {
    const mixpanel = useMixpanel();
    const ButtonComponent = rank === 'primary' ? InlineAlertPrimaryButton : InlineAlertSecondaryButton;
    const { severity } = useContext(AlertContext);
    const variantProps: Pick<ButtonProps, 'variant'> | undefined =
        rank === 'primary' && severity === 'info' ? { variant: 'outlined' } : undefined;

    return (
        <ButtonComponent
            {...variantProps}
            {...props}
            onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                mixpanel?.track('Click telemetry status button', {
                    spotterId,
                    notificationId,
                    href: props.href,
                    buttonText: e.currentTarget.textContent,
                });
                onClick?.(e);
            }}
        />
    );
};

const LearnMore = ({ rank = 'primary', ...props }: SetOptional<StatusMessageButtonProps, 'rank'>) => (
    <StatusMessageButton rank={rank} {...props}>
        Learn more
    </StatusMessageButton>
);

type ContactSupportProps = { subject: string; body: string };

const ContactSupport = ({ subject, body, ...props }: StatusMessageButtonProps & ContactSupportProps) => (
    <StatusMessageButton href={contactSupportHref({ subject, body })} {...props}>
        Contact Support
    </StatusMessageButton>
);

export const TELEMETRY_SERVICE_MESSAGES = [
    {
        notificationId: 'cell-good-no-sat-line',
        type: 'info',
        title: 'Avoid losing connection',
        body: 'Enable satellite telemetry for when Spotter is out of cell range.',
        buttons: (props) => <LearnMore href={LEARN_MORE_URL_ENABLE_SAT_FALLBACK} {...props} />,
    },
    {
        notificationId: 'sat-good-cell-bad',
        type: 'info',
        title: 'No cellular connection',
        body: 'Latest data was transmitted via satellite fallback.',
        buttons: (props) => <LearnMore href={LEARN_MORE_URL_CELL_NOT_WORKING} {...props} />,
    },
    {
        notificationId: 'sat-good-no-cell-line',
        type: 'info',
        title: 'Save money using cellular telemetry',
        body: 'Enable a cellular line subscription to avoid high satellite transmission costs.',
        buttons: (props) => (
            <StatusMessageButton rank="primary" href={`/telemetry#${spotterIdHash(props.spotterId)}`} {...props}>
                Enable cellular
            </StatusMessageButton>
        ),
    },
    {
        notificationId: 'cell-bad-no-sat-line',
        type: 'error',
        title: 'Enable satellite telemetry',
        body: 'Your Spotter may be able to reconnect using a satellite line.',
        buttons: (props) => (
            <>
                <ContactSupport
                    rank="secondary"
                    subject={`Spotter cell problem: ${props.spotterId}`}
                    body={`Hi Spotter support, ${props.spotterId} has not been successfully transmitting via celluar, and has no satellite line enabled.\n\n<Please add more details here>\n`}
                    {...props}
                />
                <StatusMessageButton rank="primary" href={`/telemetry#${spotterIdHash(props.spotterId)}`} {...props}>
                    Enable satellite
                </StatusMessageButton>
            </>
        ),
    },
    {
        notificationId: 'cell-bad-no-sat-credits',
        type: 'error',
        title: 'Neither cellular nor satellite transmission has been received recently',
        body: "Satellite transmission may not be working because you don't have any more satellite credits.",
        buttons: (props) => (
            <>
                <ContactSupport
                    rank="secondary"
                    subject={`Spotter cell problem: ${props.spotterId}`}
                    body={`Hi Spotter support, ${props.spotterId} has not been successfully transmitting via cellular, and has no satellite credits available.\n\n<Please add more details here>\n`}
                    {...props}
                />
                <StatusMessageButton rank="primary" href="/payment/satelliteCredits/onetime" {...props}>
                    Add credits
                </StatusMessageButton>
            </>
        ),
    },
    {
        notificationId: 'cell-bad-sat-bad',
        type: 'error',
        title: 'Neither cellular nor satellite transmission has been received recently',
        body: 'Your Spotter may be able to reconnect later or you can contact Support to troubleshoot.',
        buttons: (props) => (
            <>
                <ContactSupport
                    rank="primary"
                    subject={`Spotter telemetry problem: ${props.spotterId}`}
                    body={`Hi Spotter support, ${props.spotterId} has not been successfully transmitting via satellite or cellular.\n\n<Please add more details here>\n`}
                    {...props}
                />
            </>
        ),
    },
    {
        notificationId: 'sat-bad-no-cell-hardware',
        type: 'error',
        title: 'Satellite transmission has not been received recently',
        body: 'Contact Support or retrieve Spotter for troubleshooting.',
        buttons: (props) => (
            <>
                <ContactSupport
                    rank="primary"
                    subject={`Spotter satellite problem: ${props.spotterId}`}
                    body={`Hi Spotter support, ${props.spotterId} has not been successfully transmitting via satellite.\n\n<Please add more details here>\n`}
                    {...props}
                />
            </>
        ),
    },
    {
        notificationId: 'sat-bad-no-cell-line',
        type: 'error',
        title: 'Satellite transmission has not been recently received and cellular is not enabled',
        body: 'Contact Support or retrieve Spotter for troubleshooting.',
        buttons: (props) => (
            <>
                <ContactSupport
                    rank="primary"
                    subject={`Spotter satellite problem: ${props.spotterId}`}
                    body={`Hi Spotter support, ${props.spotterId} has not been successfully transmitting via satellite, and has no cellular service enabled.\n\n<Please add more details here>\n`}
                    {...props}
                />
            </>
        ),
    },
    {
        notificationId: 'no-lines',
        type: 'error',
        title: 'Enable telemetry',
        body: 'Your Spotter will attempt to reconnect once enabled.',
        buttons: (props) => (
            <>
                <ContactSupport
                    rank="secondary"
                    subject={`Spotter telemetry problem: ${props.spotterId}`}
                    body={`Hi Spotter support, ${props.spotterId} has no telemetry lines enabled.\n\n<Please add more details here>\n`}
                    {...props}
                />
                <StatusMessageButton rank="primary" href={`/telemetry#${spotterIdHash(props.spotterId)}`} {...props}>
                    Enable telemetry
                </StatusMessageButton>
            </>
        ),
    },
    {
        notificationId: 'no-cell-hardware-no-sat-credits',
        type: 'error',
        title: 'Satellite transmission has not been received recently',
        body: "Satellite transmission may not be working because you don't have any more satellite credits.",
        buttons: (props) => (
            <>
                <ContactSupport
                    rank="secondary"
                    subject={`Spotter telemetry problem: ${props.spotterId}`}
                    body={`Hi Spotter support, ${props.spotterId} has no telemetry lines enabled, and has no available satellite credits.\n\n<Please add more details here>\n`}
                    {...props}
                />
                <StatusMessageButton rank="primary" href="/payment/satelliteCredits/onetime" {...props}>
                    Add credits
                </StatusMessageButton>
            </>
        ),
    },
] as const satisfies readonly TelemetryServiceMessage[];

export const CELLULAR_ERROR_MESSAGES = [
    {
        notificationId: 'cell-memory-full',
        type: 'error',
        title: CELLULAR_ALERT_MESSAGING.cellMemoryFull.title,
        body: CELLULAR_ALERT_MESSAGING.cellMemoryFull.body,
        buttons: (props) => (
            <>
                <ContactSupport
                    rank="primary"
                    subject={`Spotter cell problem: ${props.spotterId}`}
                    body={`Hi Spotter support, ${props.spotterId} has a Cellular memory full error and is not transmitting over cellular.\n\n`}
                    {...props}
                />
            </>
        ),
    },
    {
        notificationId: 'cell-modem-init-error',
        type: 'error',
        title: CELLULAR_ALERT_MESSAGING.initError.title,
        body: CELLULAR_ALERT_MESSAGING.initError.body,
        buttons: (props) => (
            <>
                <ContactSupport
                    rank="secondary"
                    subject={`Spotter cell problem: ${props.spotterId}`}
                    body={`Hi Spotter support, ${props.spotterId} has a Cellular modem initialization error and is not transmitting over cellular.\n\n`}
                    {...props}
                />
                <StatusMessageButton rank="primary" href={LEARN_MORE_URL_CELL_NOT_WORKING} {...props}>
                    Learn more
                </StatusMessageButton>
            </>
        ),
    },
] as const satisfies readonly TelemetryServiceMessage[];

export type TelemetryServiceNotificationId =
    | (typeof TELEMETRY_SERVICE_MESSAGES)[number]['notificationId']
    | (typeof CELLULAR_ERROR_MESSAGES)[number]['notificationId'];

export const whichTelemetryServiceMessage = (
    signalStatus: DeviceSignalStatus | undefined,
    subStatus: DeviceSubscriptionStatus | undefined,
    cellModemError: SpotterSystemError | null | undefined
): TelemetryServiceNotificationId | null => {
    if (!signalStatus || !subStatus || cellModemError === undefined) {
        return null;
    }

    const { isLateToCheckin, lastUpdateSource } = signalStatus;

    const { cellCapableButNoService, satCapableButNoService, cellEnabled, satEnabled, outOfSatelliteCredits } =
        subStatus;

    if (
        subStatus.cellEnabled &&
        cellModemError &&
        !INSUFFICIENT_SIGNAL_ERRORS.some((errorCode) => errorCode === cellModemError.code)
    ) {
        if (cellModemError.code === 'WARN_CELL_MEMORY_FULL') {
            return 'cell-memory-full';
        }
        return 'cell-modem-init-error';
    }

    if (!isLateToCheckin) {
        if (lastUpdateSource === 'iridium' && cellCapableButNoService) {
            return 'sat-good-no-cell-line';
        }
        if (lastUpdateSource === 'cellular' && satCapableButNoService) {
            return 'cell-good-no-sat-line';
        }
        if (lastUpdateSource === 'iridium' && cellEnabled) {
            return 'sat-good-cell-bad';
        }
        return null;
    } else {
        if (cellEnabled && !satEnabled) {
            if (outOfSatelliteCredits) return 'cell-bad-no-sat-credits';
            return 'cell-bad-no-sat-line';
        }
        if (cellEnabled && satEnabled) {
            return 'cell-bad-sat-bad';
        }
        if (satEnabled) {
            if (cellCapableButNoService) return 'sat-bad-no-cell-line';
            return 'sat-bad-no-cell-hardware';
        }
        if (outOfSatelliteCredits) {
            return 'no-cell-hardware-no-sat-credits';
        }
        return 'no-lines';
    }
};

export const TelemetryServiceStatusMessage = ({ spotterId }: { spotterId: string }) => {
    const { data: signalStatus } = useDeviceSignalStatus({ spotterId });
    const { data: subStatus } = useDeviceSubscriptionStatus({ spotterId });
    const { data: cellularSignalStatus } = useDeviceCellularSignalStatus({ spotterId });

    const notificationId = useMemo(
        () => whichTelemetryServiceMessage(signalStatus, subStatus, cellularSignalStatus?.error),
        [signalStatus, subStatus, cellularSignalStatus]
    );

    const message: TelemetryServiceMessage | null = useMemo(
        () =>
            (find([...TELEMETRY_SERVICE_MESSAGES, ...CELLULAR_ERROR_MESSAGES], {
                notificationId,
            }) as TelemetryServiceMessage) ?? null,
        [notificationId]
    );

    if (!message) {
        return <></>;
    }

    return <StatusMessageBox message={message} spotterId={spotterId} />;
};

/**
 * This is factored out of TelemetryServiceStatusMessage just to make it easier show a catalog of all the options in {@link DemoServiceStatusMessageCatalog}
 */
const StatusMessageBox = ({ spotterId, message }: { spotterId: string; message: TelemetryServiceMessage }) => {
    const { type, title, body, buttons, notificationId } = message;
    return (
        <Stack gap={1}>
            <InlineAlert
                size="small"
                icon={false}
                severity={type}
                actionButton={buttons({ spotterId, notificationId })}
                dismissNotificationId={notificationId}
                notificationContext={{ spotterId }}
            >
                <InlineAlertTitle>{title}</InlineAlertTitle>
                <InlineAlertBody>{body}</InlineAlertBody>
            </InlineAlert>
        </Stack>
    );
};

export const DemoServiceStatusMessageCatalog = () => {
    const { undismissNotification } = useNotifications();
    useEffect(() => {
        for (const { notificationId } of TELEMETRY_SERVICE_MESSAGES) {
            undismissNotification(notificationId, { spotterId: 'SPOT-DEMO' });
        }
    }, [undismissNotification]);
    return (
        <>
            {TELEMETRY_SERVICE_MESSAGES.map((message) => (
                <Stack key={message.notificationId} sx={{ m: 1 }}>
                    <Typography>{lowerCase(message.notificationId)}:</Typography>
                    <StatusMessageBox message={message} spotterId="SPOT-DEMO" />
                </Stack>
            ))}
        </>
    );
};
